import { useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, } from "recharts";
import useApi from "../../hooks/useApi";
import { transactionGraph } from "../../constants/API/API";

const FILTERS = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Yearly", value: "yearly" },
    { label: "Last 5 Yrs", value: "last5years" },
];

const BLUE = "#ff5722";
const AMBER = "#494949";
const TEAL = "#1d9e75";

const fmtCurrency = (v) => {
    if (v >= 10_00_000) return `${(v / 10_00_000).toFixed(1)}CAD L`;
    if (v >= 1_000) return `${Math.round(v / 1_000)}CAD k`;
    return `${v} CAD`;
};

const fmtCount = (v) => {
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return `${v}`;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const revenue = payload.find((p) => p.dataKey === "amount");
    const txns = payload.find((p) => p.dataKey === "transactions");

    return (
        <div style={{
            background: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10,
            padding: "10px 14px",
            minWidth: 170,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
            <p style={{ margin: "0 0 8px", fontSize: 14, color: "#888", fontWeight: 500, letterSpacing: "0.04em" }}>
                {label}
            </p>
            {revenue && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, color: BLUE, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: BLUE, display: "inline-block" }} />
                        Revenue
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#414141" }}>
                        {fmtCurrency(revenue.value)}
                    </span>
                </div>
            )}
            {txns && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <span style={{ fontSize: 14, color: AMBER, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: AMBER, display: "inline-block" }} />
                        Transactions
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#414141" }}>
                        {fmtCount(txns.value)}
                    </span>
                </div>
            )}
        </div>
    );
};

const MetricCard = ({ label, value, sub, accentColor }) => (
    <div style={{
        background: "rgba(50, 50, 50, 0.03)",
        border: "0.5px solid #ddd",
        borderRadius: 10,
        padding: "12px 16px",
        flex: 1,
        minWidth: 0,
    }}>
        <p style={{ margin: "0 0 4px", fontSize: 14, color: "#666", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: "600" }}>
            {label}
        </p>
        <p style={{ margin: "0 0 2px", fontSize: 22, fontWeight: 600, color: accentColor || "#e5e5e5", lineHeight: 1.2 }}>
            {value}
        </p>
        {sub && (
            <p style={{ margin: 0, fontSize: 11, color: "#4ade80" }}>{sub}</p>
        )}
    </div>
);

const GradientBar = (props) => {
    const { x, y, width, height } = props;
    const id = `grad-${x}`;
    if (!height || height <= 0) return null;
    return (
        <g>
            <defs>
                <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={BLUE} stopOpacity={0.15} />
                </linearGradient>
            </defs>
            <rect
                x={x} y={y} width={width} height={height}
                rx={5} ry={5}
                fill={`url(#${id})`}
                stroke={BLUE}
                strokeWidth={0.5}
                strokeOpacity={0.4}
            />
        </g>
    );
};

const TransactionGraph = () => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("daily");

    useApi({
        callback: transactionGraph,
        payload: { filter },
        dependencies: [filter],
        shouldCallApi: true,

        onSuccess: (data) => {
            const formatted = data?.list?.map((item) => ({
                name: item.name,
                amount: item.totalAmount,
                transactions: item.totalTransactions,
            }));
            setChartData(formatted || []);
            setLoading(false);
        },

        onError: () => setLoading(false),
    });

    const totalRevenue = chartData.reduce((a, b) => a + (b.amount || 0), 0);
    const totalTxns = chartData.reduce((a, b) => a + (b.transactions || 0), 0);
    const avgPerTxn = totalTxns ? Math.round(totalRevenue / totalTxns) : 0;

    if (loading) {
        return (
            <div className="transaction-graph-card">
                <div className="transaction-graph-skeleton-block" style={{ width: "40%", height: 20, marginBottom: 20 }} />
                <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="transaction-graph-skeleton-block" style={{ flex: 1, height: 72 }} />
                    ))}
                </div>
                <div className="transaction-graph-skeleton-block" style={{ width: "100%", height: 300 }} />
            </div>
        );
    }

    return (
        <div className="transaction-graph-card">

            <div className="transaction-graph-header">
                <div className="transaction-graph-title-group">
                    <div className="transaction-graph-icon-box">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <rect x="1" y="8" width="3" height="7" rx="1" fill={BLUE} />
                            <rect x="6" y="5" width="3" height="10" rx="1" fill={BLUE} fillOpacity={0.65} />
                            <rect x="11" y="2" width="3" height="13" rx="1" fill={BLUE} fillOpacity={0.35} />
                            <path d="M2.5 7 L7.5 4 L12.5 1" stroke={AMBER} strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </div>
                    <span className="transaction-graph-title">Revenue vs Transactions</span>
                </div>

                <div className="transaction-graph-filter-group">
                    {FILTERS.map((f) => (
                        <button
                            key={f.value}
                            onClick={() => { setLoading(true); setFilter(f.value); }}
                            className={"transaction-graph-filter-btn " + (filter === f.value ? "active" : "")}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="transaction-graph-metrics">
                <MetricCard
                    label="Total Revenue"
                    value={fmtCurrency(totalRevenue)}
                    accentColor={BLUE}
                />
                <MetricCard
                    label="Transactions"
                    value={fmtCount(totalTxns)}
                    accentColor={AMBER}
                />
                <MetricCard
                    label="Avg / Txn"
                    value={`${avgPerTxn.toLocaleString()} CAD`}
                    accentColor={TEAL}
                />
            </div>

            <ResponsiveContainer width="100%" height={340}>
                <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
                    barCategoryGap="35%"
                >
                    <defs>
                        <linearGradient id="lineGlow" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={AMBER} stopOpacity={0.15} />
                            <stop offset="100%" stopColor={AMBER} stopOpacity={0} />
                        </linearGradient>
                    </defs>

                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={false}
                        vertical={false}
                    />

                    <XAxis
                        dataKey="name"
                        tick={{ fill: "#666", fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: "#ccc" }}
                        dy={6}
                    />

                    <YAxis
                        yAxisId="left"
                        tick={{ fill: BLUE, fontSize: 11 }}
                        axisLine={{ stroke: "#ccc" }}
                        tickLine={false}
                        tickFormatter={fmtCurrency}
                        width={52}
                    />

                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: AMBER, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={fmtCount}
                        width={44}
                    />

                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />

                    <Bar
                        yAxisId="left"
                        dataKey="amount"
                        name="Revenue"
                        shape={<GradientBar />}
                        maxBarSize={40}
                    />

                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="transactions"
                        name="Transactions"
                        stroke={AMBER}
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: AMBER, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: AMBER, stroke: "rgba(239,159,39,0.3)", strokeWidth: 4 }}
                        fill="url(#lineGlow)"
                    />
                </ComposedChart>
            </ResponsiveContainer>

            <div className="transaction-graph-legend">
                <span className="transaction-graph-legend-item">
                    <span className="transaction-graph-legend-dot transaction-graph-legend-dot-revenue" />
                    Revenue (CAD)
                </span>
                <span className="transaction-graph-legend-item">
                    <span className="transaction-graph-legend-dot transaction-graph-legend-dot-txn" />
                    Transactions
                </span>
                <span className="transaction-graph-legend-item">
                    <span className="transaction-graph-legend-dot transaction-graph-legend-dot-avg" />
                    Avg / Txn
                </span>
            </div>
        </div>
    );
};

export default TransactionGraph;